import { AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { dialog, type DialogRendererProps } from "@/lib/dynamic-dialog-state";
import { Trash2Icon } from "lucide-react";
import { type ReactNode } from "react";
import { IconWrapper } from "./atoms/icon-wrapper";

type FunctionPropWithDialogProps = (props: DialogRendererProps<boolean>) => ReactNode;

export interface ConfirmDialogProps {
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  confirmTextButton?: ReactNode | FunctionPropWithDialogProps;
  cancelTextButton?: ReactNode | FunctionPropWithDialogProps;
}

export function ConfirmDialog(props: ConfirmDialogProps & DialogRendererProps<boolean>) {
  const isFunction = (value: unknown): value is FunctionPropWithDialogProps => typeof value === "function";
  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 items-start">
          {props.icon ||
            <IconWrapper size='default' variant='destructive' background={'opaque'}>
                <Trash2Icon />
            </IconWrapper>
          }

          <div>
            <AlertDialogTitle className="text-lg font-semibold text-foreground">
              {props.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              {props.description}
            </AlertDialogDescription>
          </div>
        </div>
      </AlertDialogHeader>
      {props.children}
      <div className="flex justify-end pt-2 space-x-3">
        {isFunction(props.cancelTextButton) ? props.cancelTextButton(props) : (
          <Button
            type="button"
            onClick={() => props.cancel()}
            variant={"outline"}
            size='sm'
          >
            {props.cancelTextButton || "Cancel"}
          </Button>
        )}
        {isFunction(props.confirmTextButton) ? props.confirmTextButton(props) : (
          <Button
            variant={"destructive"}
            type="submit"
            onClick={() => props.confirm()}
            size='sm'
          >
            {props.confirmTextButton || "Confirm"}
          </Button>
        )}
      </div>
    </AlertDialogContent>
  );
}

export const confirmDialog = dialog(ConfirmDialog);
